import React from "react";
import FloatingActionButton from "./FloatingActionButton";
import FloatingActionButtonChild from "./FloatingActionButtonChild";
import { ReactComponent as AddIcon } from "assets/icons/add.svg";
import { ReactComponent as RemoveIcon } from "assets/icons/remove.svg";
import { ReactComponent as AdjustIcon } from "assets/icons/equal.svg";
import { ReactComponent as BarcodeIcon } from "assets/icons/barcode.svg";
import useLayout from "hooks/useLayout";
import { ReactComponent as OrdersIcon } from "assets/icons/orders.svg";
import { useTranslation } from "react-i18next";
import useStorages from "hooks/useStorages";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { setBarcodeScanHandler } from "@/utils/barcodeScanHandler";
import { fetchBarcodeMapping } from "@/hooks/useBarcodes";

interface FloatingActionButtonStockProps {
    storageId: string;
}

const FloatingActionButtonStock: React.FC<FloatingActionButtonStockProps> = ({ storageId }) => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const {
        openDialog,
        closeDialog,
        addToast,
    } = useLayout()

    const { isActionsRequiredToBeLinkedToTransactions, isStockAdjustmentEnabled } = useStorages({ storageId })
    const requireActionsLinkedToTransactions = isActionsRequiredToBeLinkedToTransactions()
    const stockAdjustmentEnabled = isStockAdjustmentEnabled()

    const handleScanBarcode = () => {
        setBarcodeScanHandler(async (barcode: string) => {
            const mapping = await fetchBarcodeMapping(queryClient, storageId, barcode)
            if (!mapping?.productId) {
                addToast({ text: t('toastBarcodeNotFound'), colorScheme: 'red' })
                return false
            }
            closeDialog()
            navigate(`/storage/products/${mapping.productId}`)
            return true
        })
        openDialog('scan_barcode')
    }

    return (
        <FloatingActionButton icon={<OrdersIcon />}>
            <FloatingActionButtonChild icon={<BarcodeIcon />} text={t('buttonScanBarcode')} onClick={handleScanBarcode} />
            {requireActionsLinkedToTransactions ? (
                <>
                    <FloatingActionButtonChild icon={<AddIcon />} text={t('buttonNewIncoming')} to={`/storage/transactions/create/incoming`} />
                    <FloatingActionButtonChild icon={<RemoveIcon />} text={t('buttonNewOutgoing')} to={`/storage/transactions/create/outgoing`} />
                    {stockAdjustmentEnabled && (
                        <FloatingActionButtonChild icon={<AdjustIcon />} text={t(`adjustStockButton`)} onClick={() => openDialog(`adjust_product_stock`)} />
                    )}
                </>
            ) : (
                <>
                    <FloatingActionButtonChild icon={<AddIcon />} text={t(`addStockButton`)} onClick={() => openDialog(`add_to_stock`)} />
                    <FloatingActionButtonChild icon={<RemoveIcon />} text={t(`removeStockButton`)} onClick={() => openDialog(`remove_from_stock`)} />
                    {stockAdjustmentEnabled && (
                        <FloatingActionButtonChild icon={<AdjustIcon />} text={t(`adjustStockButton`)} onClick={() => openDialog(`adjust_product_stock`)} />
                    )}
                </>
            )}
        </FloatingActionButton>
    );
};

export default FloatingActionButtonStock;
